import { MonthDayProps } from '@oberoninternal/travelbase-ds/components/calendar/PlannerMonth';
import Special from '@oberoninternal/travelbase-ds/svg/Special.svg';
import Package from '@oberoninternal/travelbase-ds/svg/Package.svg';
import validInterval from '@oberoninternal/travelbase-ds/utils/validInterval';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import isWithinInterval from 'date-fns/isWithinInterval';
import startOfToday from 'date-fns/startOfToday';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import parseToDateString from '../../constants/parseToDateString';
import { PlannerTripFragment, TripTypeEnum } from '../../generated/graphql';
import { getDateStringsFromTrip } from '../../utils/trip';
import { PlannerState } from './usePlannerPeriod';
import isAfter from 'date-fns/isAfter';

const containsDiscount = (trip: PlannerTripFragment) =>
    !!(trip.type !== TripTypeEnum.Regular && trip.originalPrice && trip.price < trip.originalPrice);
const containsUpsell = (trip: PlannerTripFragment) =>
    trip.type !== TripTypeEnum.Regular &&
    (!trip.originalPrice || (trip.originalPrice && trip.price > trip.originalPrice));
const getDayIcons = (discountAvailable: boolean, upsellAvailable: boolean) => (
    <>
        {discountAvailable && <Special />}
        {upsellAvailable && <Package />}
    </>
);
const usePlannerModifier = (state: PlannerState, trips: Map<string, PlannerTripFragment[]>) => {
    const intl = useIntl();
    const longestTrips = useMemo(
        () => [...trips.entries()].map(([, dayTrips]) => [...dayTrips].sort((a, b) => b.duration - a.duration)[0]),
        [trips]
    );
    const { period, selecting } = state;
    const getDayTrips = useCallback((date: Date | number) => trips.get(parseToDateString(date)) ?? [], [trips]);
    // These trips are sorted by price
    const potentialDayTrips = useMemo(
        () => (period?.start ? [...getDayTrips(period.start)].sort((a, b) => a.price - b.price) : undefined),
        [getDayTrips, period]
    );

    const formatPrice = useCallback((price: number) => intl.formatNumber(price, { format: 'EUR_ROUNDED' }), [intl]);
    const computeDayProps = useCallback(
        (day: Date): MonthDayProps => {
            const { start, end } = period ?? {};

            if (isBefore(day, startOfToday())) {
                return { variant: 'disabled' };
            }
            const dayTrips = getDayTrips(day);

            if (start && isSameDay(day, start)) {
                const discountAvailable = dayTrips.some(containsDiscount);
                const upsellAvailable = dayTrips.some(containsUpsell);

                return {
                    variant: 'selected-start',
                    icon: getDayIcons(discountAvailable, upsellAvailable),
                };
            }

            if (start && end && isSameDay(day, end) && potentialDayTrips) {
                const sameDurationTrips = potentialDayTrips.filter(
                    ({ duration }) => differenceInCalendarDays(end, start) === duration
                );
                const discountAvailable = sameDurationTrips.some(containsDiscount);
                const upsellAvailable = dayTrips.some(containsUpsell);

                const [bestTrip] = sameDurationTrips;
                const label = bestTrip ? formatPrice(bestTrip.price) : undefined;

                return {
                    variant: 'selected-end',
                    label,
                    icon: getDayIcons(discountAvailable, upsellAvailable),
                };
            }

            if (selecting && start && potentialDayTrips) {
                for (const { duration, price } of potentialDayTrips) {
                    const label = formatPrice(price);
                    const discountAvailable = potentialDayTrips.some(
                        trip => trip.duration === duration && containsDiscount(trip)
                    );
                    const upsellAvailable = dayTrips.some(containsUpsell);

                    const potential = addDays(start, duration);
                    if (isSameDay(potential, day)) {
                        if (end && isWithinInterval(day, { start, end })) {
                            return {
                                variant: 'bookable-within',
                                label,
                                icon: getDayIcons(discountAvailable, upsellAvailable),
                            };
                        }
                        return {
                            variant: 'bookable-outside',
                            label,
                            icon: getDayIcons(discountAvailable, upsellAvailable),
                        };
                    }
                }
            }

            if (validInterval(period) && isAfter(period.end, period.start) && isWithinInterval(day, period)) {
                const discountAvailable = dayTrips.some(containsDiscount);
                const upsellAvailable = dayTrips.some(containsUpsell);

                return {
                    variant: !selecting && dayTrips.length > 0 ? 'bookable-within' : 'within',
                    icon: getDayIcons(discountAvailable, upsellAvailable),
                };
            }

            if (!selecting) {
                const discountAvailable = dayTrips.some(containsDiscount);
                const upsellAvailable = dayTrips.some(containsUpsell);

                if (dayTrips.length > 0) {
                    return {
                        variant: 'bookable',
                        icon: getDayIcons(discountAvailable, upsellAvailable),
                    };
                }
            }

            if (
                longestTrips.some(trip => {
                    const { arrivalDate, departureDate } = getDateStringsFromTrip(trip);
                    return isWithinInterval(day, { start: new Date(arrivalDate), end: new Date(departureDate) });
                })
            ) {
                return { variant: 'default' };
            }

            return { variant: 'blocked' };
        },
        [formatPrice, getDayTrips, period, potentialDayTrips, selecting, longestTrips]
    );

    return computeDayProps;
};

export default usePlannerModifier;
